// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';

import Box from 'sail/Box';
import FlagIcon from 'sail/FlagIcon';
import {preloadedQuery, SearchableSelect, useSearch} from 'sail/Search';
import {Text} from 'sail/Text';

import {Badge} from 'docs/components/core/Badge/Badge';
import type {CountryDisplayName} from 'docs/components/core/CountryName/CountryName';
import {usePrefs, useLocation} from 'docs/data';
import {useActivePage} from 'docs/hooks';
import {useContentAnimation} from 'docs/shell/content/ContentAnimationProvider';
import type {TerminalContext} from 'docs/types';

import './TerminalCountrySelector.scss';

type CompatibilityData = $PropertyType<TerminalContext, 'compatibility_data'>;

const PREF_KEY = 'integration-country';

const messages = defineMessages({
  country: {
    id: 'docsShell.TerminalCountrySelector.country',
    description: 'Dropdown label. Country',
    defaultMessage: 'Country',
  },
});

const isCountryInBeta = (
  countryCompatibilityData: $Values<CompatibilityData> | null,
): boolean => {
  // There's an edge case where a user may set their selected country pref to an invite-only country
  // and then log out but the pref doesn't clear, causing compatibilityDataForCountry to be undefined
  // If this happens, we should assume the country is in beta
  return countryCompatibilityData?.in_beta ?? true;
};

type TerminalCountrySelectorProps = {|
  compatibilityData: CompatibilityData | string,
  countryNames: Array<CountryDisplayName>,
  fallbackCountry: string,
|};

export const TerminalCountrySelector = ({
  compatibilityData: compatibilityDataOfUnknownType,
  countryNames,
  fallbackCountry,
}: TerminalCountrySelectorProps) => {
  // In https://git.corp.stripe.com/stripe-internal/pay-server/pull/307173,
  // we changed the compatibility_data field of the terminal context from
  // a string that has to get JSON.parse()'d to a struct that docs_renderer automatically
  // converts to the proper object shape. While we transition though, we need to be able
  // to handle both because docs deploys just a bit earlier than site-srv so
  // site-srv would continue sending a string even though this code is expecting an object
  // TODO(bibek, 2020-04-15) Remove this conditional logic once site-srv has been deployed with the updated context field type
  const compatibilityData: CompatibilityData = React.useMemo(
    () =>
      typeof compatibilityDataOfUnknownType === 'string'
        ? JSON.parse(compatibilityDataOfUnknownType)
        : compatibilityDataOfUnknownType,
    [compatibilityDataOfUnknownType],
  );

  const countryOptions: Array<CountryDisplayName> = React.useMemo(
    () =>
      Object.keys(compatibilityData)
        .map((countryCode) =>
          countryNames.find((def) => def.code === countryCode),
        )
        .filter(Boolean)
        // Sort by country name
        .sort((a, b) => {
          return a.name && b.name
            ? a.name.localeCompare(b.name)
            : a.code.localeCompare(b.code);
        }),
    [compatibilityData, countryNames],
  );

  const hasInitialized = React.useRef(false);
  const {getPref, setPref} = usePrefs();
  const activePage = useActivePage();
  const {setAnimationDirection} = useContentAnimation();
  const {location} = useLocation();

  const getCountryIndex = React.useCallback(
    (country) => countryOptions.findIndex((_c) => _c.code === country),
    [countryOptions],
  );

  const [selectedCountry, setSelectedCountry] = React.useState(
    getPref<string>(PREF_KEY),
  );
  const updateUserCountry = React.useCallback(
    (country: string, updateQueryParams = true) => {
      setPref(PREF_KEY, country);
      setSelectedCountry(country);
    },
    [setPref],
  );

  const handleChange = React.useCallback(
    (countryDisplayName: ?CountryDisplayName) => {
      if (!countryDisplayName) {
        return;
      }
      const country = countryDisplayName.code;
      setAnimationDirection(
        getCountryIndex(activePage.prefs[PREF_KEY]),
        getCountryIndex(country),
      );
      updateUserCountry(country);
    },
    [setAnimationDirection, getCountryIndex, activePage, updateUserCountry],
  );

  // When the page loads, we either need to sync the query param to prefs or vice versa, query param takes precedence
  React.useEffect(() => {
    if (!hasInitialized.current) {
      const countryParam = location.query['integration-country'];
      if (countryParam) {
        updateUserCountry(countryParam, false);
      } else if (selectedCountry) {
        updateUserCountry(selectedCountry);
      } else {
        // No query param, no pref, fall back to merchant country
        updateUserCountry(fallbackCountry);
      }
      hasInitialized.current = true;
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [setPref, location, selectedCountry]);

  const getSearchableText = React.useCallback(
    (countryDisplayName: CountryDisplayName) =>
      countryDisplayName.name ?? countryDisplayName.code,
    [],
  );

  const getButtonLabel = React.useCallback(
    (countryDisplayName: CountryDisplayName) => (
      <Box flex={{alignItems: 'center'}}>
        <Box margin={{right: 8}}>
          {/* $FlowExpectedError FlagIcon is very strongly typed but `country` is typed as any string; it's fine though since it'll only be proper countries */}
          <FlagIcon countryCode={countryDisplayName.code} />
        </Box>
        <Text color="gray">{countryDisplayName.name}</Text>
        {isCountryInBeta(compatibilityData[countryDisplayName.code]) && (
          <Badge color="green" label="Beta" />
        )}
      </Box>
    ),
    [compatibilityData],
  );
  const renderCountryOption = (renderProps: {entry: CountryDisplayName}) =>
    getButtonLabel(renderProps.entry);

  const queryCountries = React.useMemo(
    () => preloadedQuery(getSearchableText, countryOptions),
    [countryOptions, getSearchableText],
  );

  const searchProps = useSearch(queryCountries);
  return (
    <Box margin={{top: 12}} className="TerminalCountrySelector">
      <Box margin={{bottom: 8}}>
        <Text size={14} weight="medium">
          <FormattedMessage {...messages.country} />
        </Text>
      </Box>
      <SearchableSelect
        {...searchProps}
        size="large"
        width={280}
        inputPlaceholder="Search…"
        chooseLabel="Choose a country…"
        getSearchableText={getSearchableText}
        value={countryNames.find((cn) => cn.code === selectedCountry)}
        renderItem={renderCountryOption}
        onChange={handleChange}
        getButtonLabel={getButtonLabel}
      />
    </Box>
  );
};
